import "./index.css";
import "react-toastify/dist/ReactToastify.css";
import { datadogRum } from "@datadog/browser-rum";
import { reactPlugin } from "@datadog/browser-rum-react";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faCopy } from "@fortawesome/free-regular-svg-icons";
import {
  faBars,
  faCheck,
  faEdit,
  faMicrophone,
  faPaw,
  faStop,
  faTimes,
  faWeight,
} from "@fortawesome/free-solid-svg-icons";
import * as Sentry from "@sentry/react";
import { asyncWithLDProvider } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import { createRoot } from "react-dom/client";
import { ToastContainer } from "react-toastify";

import Providers from "./components/index/providers";
import App from "./components/layout/App";
import { ErrorBoundary } from "./components/layout/ErrorBoundary";
import { getConfig } from "./config";
import { IGNORED_AUTH_ERRORS } from "./constants";
import { isMCloudIFrame, setMCloudEmbedded } from "./utils";

/** LOGOUT PREVIOUS USER IF EMBEDDED */
if (isMCloudIFrame()) {
  localStorage.clear();
  setMCloudEmbedded();
  window.location.href = `${
    getConfig().flask
  }/sso/mvp/logout?origin=${window.location.origin}`;
}

/** BEGIN SENTRY CONFIG */
if (getConfig().sentry.enabled) {
  Sentry.init({
    dsn: getConfig().sentry.dsn,
    environment: getConfig().environmentName,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: getConfig().sentry.tracesSampleRate,
    ignoreErrors: IGNORED_AUTH_ERRORS,
    beforeSend: (event) => {
      if (event.exception?.values?.[0]?.stacktrace?.frames) {
        const hasAppFrame =
          event.exception.values[0].stacktrace.frames.some(
            (frame) => !frame.filename?.includes("node_modules"),
          );

        if (!hasAppFrame) {
          return null;
        }
      }
      return event;
    },
  });
  //Set the web app version
  Sentry.setTag("web_app_version", import.meta.env.VITE_APP_VERSION);
}
/** END SENTRY CONFIG */

/** BEGIN DATADOG RUM CONFIG */
if (getConfig().datadog.enabled) {
  datadogRum.init({
    applicationId: getConfig().datadog.applicationId,
    clientToken: getConfig().datadog.clientToken,
    site: getConfig().datadog.site,
    service: getConfig().datadog.service,
    env: getConfig().datadog.env,
    version: import.meta.env.VITE_APP_VERSION,
    sessionSampleRate: getConfig().datadog.sessionSampleRate,
    sessionReplaySampleRate:
      getConfig().datadog.sessionReplaySampleRate,
    defaultPrivacyLevel: getConfig().datadog.defaultPrivacyLevel,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    allowedTracingUrls: [
      (url) => {
        const apiOrigin = new URL(getConfig().flask).origin;
        return new URL(url).origin === apiOrigin;
      },
    ],
    plugins: [reactPlugin({ router: true })],
  });
}
/** END DATADOG RUM CONFIG */

mixpanel.init(getConfig().mixpanel.id, {
  debug: getConfig().mixpanel.debug,
  cookie_domain: getConfig().mixpanel.cookie_domain,
  cross_subdomain_cookie: getConfig().mixpanel.cross_subdomain_cookie,
});

(function setupAutomaticTracking() {
  /**
   * Returns the label that should be used to track metrics for the given element
   * @returns The first data-mx-label attribute in the hierarchy, null otherwise
   */
  function metricsLabel(element) {
    while (
      element instanceof HTMLElement ||
      element instanceof SVGElement
    ) {
      const label = element.dataset?.mxLabel;
      if (label) {
        return label;
      }
      element = element.parentElement;
    }
    return null;
  }

  // Set up automatic click/drag tracking. We don't use Mixpanel's autocapture because
  // it doesn't always work as expected.
  document.body.addEventListener(
    "click",
    (e) => {
      const label = metricsLabel(e.target);
      if (!label) return;
      mixpanel.track("Element Clicked", { label });
    },
    { passive: true, capture: true },
  );
  document.body.addEventListener(
    "dragstart",
    (e) => {
      const label = metricsLabel(e.target);
      if (!label) return;
      mixpanel.track("Element Dragged", { label });
    },
    { passive: true, capture: true },
  );
})();

// Disable MixPanel if not enabled in config
if (!getConfig().mixpanel.enabled) {
  mixpanel.disable();
}

// Identify the user ASAP if possible to avoid firing a Page Navigation event before Mixpanel has the user ID
// Check both user_uuid and accessCsrf to ensure session is actually valid
const userUuid = localStorage.getItem("user_uuid");
const accessCsrf = localStorage.getItem("accessCsrf");
if (
  userUuid &&
  accessCsrf &&
  userUuid !== mixpanel.get_distinct_id()
) {
  mixpanel.identify(userUuid);
}

if (getConfig().datadog.enabled && userUuid && accessCsrf) {
  datadogRum.setUser({ id: userUuid });
}

mixpanel.register({
  "Web App Version": import.meta.env.VITE_APP_VERSION,
  platform: "web",
});

library.add(
  faCopy,
  faBars,
  faMicrophone,
  faStop,
  faPaw,
  faEdit,
  faCheck,
  faTimes,
  faWeight,
);

const contextClass = {
  success: "bg-green-600",
  error: "bg-red-600",
  info: "bg-gray-600",
  warning: "bg-yellow-500",
  default: "bg-indigo-600",
  dark: "bg-gray-600 text-gray-100",
};

(async () => {
  const LDProvider = await asyncWithLDProvider({
    clientSideID: getConfig().launchdarkly.client_key,
    context: {
      key: userUuid ? `user-uuid-${userUuid}` : "anonymous-user",
    },
  });
  const root = createRoot(document.getElementById("root"));
  root.render(
    <LDProvider>
      <ErrorBoundary>
        <Providers>
          <App />
          <ToastContainer
            className={"mb-14"}
            toastClassName={({ type }) =>
              contextClass[type || "default"] +
              " my-2 relative flex p-1 min-h-10 rounded-md justify-between overflow-hidden cursor-pointer text-white"
            }
            position="bottom-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss={false}
            draggable
            pauseOnHover
            icon={false}
          />
        </Providers>
      </ErrorBoundary>
    </LDProvider>,
  );
})();
